/**
 * A "latest value" subscription registry.
 *
 * The problem this solves, learned the hard way: an async source (Firebase
 * auth) publishes its first value as soon as it starts. Any subscriber that
 * registers after that moment — because something was awaited in between —
 * never hears about it and waits forever.
 *
 * A registry that only replays truthy values makes this worse, because the
 * most important case is `null`: "we checked, and nobody is signed in." That
 * is a real answer, not the absence of one, and the UI must react to it.
 *
 * So: once a value has been published, every new subscriber receives it
 * immediately, including when that value is null.
 */

/**
 * @template T
 * @returns {{
 *   publish: (value: T) => void,
 *   subscribe: (listener: (value: T) => void) => () => void,
 *   get: () => T | undefined,
 *   hasValue: () => boolean,
 *   size: () => number,
 * }}
 */
export function createLatest() {
  /** @type {Set<(value: any) => void>} */
  const listeners = new Set()
  let value
  let published = false

  return {
    publish(next) {
      value = next
      published = true
      for (const listener of [...listeners]) {
        try {
          listener(next)
        } catch (err) {
          // One bad subscriber must not stop the others from updating.
          console.error('subscriber failed', err)
        }
      }
    },

    subscribe(listener) {
      listeners.add(listener)
      // Replay whatever is already known — including null.
      if (published) {
        try {
          listener(value)
        } catch (err) {
          console.error('subscriber failed on replay', err)
        }
      }
      return () => listeners.delete(listener)
    },

    get: () => value,
    hasValue: () => published,
    size: () => listeners.size,
  }
}
